import React, { useEffect, useRef, useState } from 'react';
import S from './Header.styled';
import { storyblokEditable } from 'gatsby-source-storyblok';
import { Link } from 'gatsby';
import { Flex, MainInner } from '@/styles/global';
import NavLink from '@/components/storyblok/NavLink';
import { IAsset, ILink } from '@/src/types';
import { Absolute, Icon, Image } from '@/components/global';
import { modifyImage } from '@/src/helpers/modifyImage';
import { AnimatePresence, motion } from 'framer-motion';
import { useSiteSettings } from '@/src/context/SiteSettingsContext';
import { BreadNormal } from '@/styles/typography';

type LinkArray = {
  label: string;
  link: ILink;
}[];

type HeaderProduct = {
  label: string;
  image: IAsset;
  links: LinkArray;
  product_links: LinkArray;
  margin_top?: boolean;
};

interface IHeaderProps {
  settings: {
    header_logo_white: IAsset;
    header_products_title: string;
    header_products: HeaderProduct[];
    header_links_1_title: string;
    header_links_1: LinkArray;
    header_links_2_title: string;
    header_links_2: LinkArray;
    banner_label: string;
    banner_label_link: ILink;
    banner_link: ILink;
    banner_text: string;
    banner_link_label: string;
  };
  text_color?: 'white' | 'black';
  position?: 'fixed' | 'absolute';
  banner?: boolean;
  desktopPosition?: 'fixed' | 'absolute';
  globalBannerSetting?: boolean;
}

const Header: React.FC<IHeaderProps> = ({
  settings,
  text_color,
  position,
  banner,
  desktopPosition,
  globalBannerSetting,
}) => {
  const {
    header_logo_white: logo,
    header_products_title: productTitle,
    header_products: headerProducts,
    header_links_1_title: links1Title,
    header_links_1: links1,
    header_links_2_title: links2Title,
    header_links_2: links2,
    banner_label,
    banner_label_link,
    banner_link,
    banner_link_label,
    banner_text,
  } = settings;
  const [isScrolled, setIsScrolled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [openProduct, setOpenProduct] = useState<HeaderProduct | undefined>(
    undefined
  );
  const headerRef = useRef<HTMLDivElement | null>(null);
  const { slug } = useSiteSettings();

  // Sticky nav
  useEffect(() => {
    if (position === 'absolute') return;

    const handleScroll = () => {
      if (headerRef.current) {
        const offsetTop = headerRef.current.offsetTop;
        if (window.pageYOffset > offsetTop) {
          setIsScrolled(true);
        } else {
          setIsScrolled(false);
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    !isOpen && setOpenProduct(undefined);
  }, [isOpen, openProduct]);

  const handleBackgroundClick = () => {
    setOpenProduct(undefined);
    setIsOpen(false);
  };

  const modLogo = modifyImage(logo, '300x60');

  return (
    <>
      {globalBannerSetting && banner && (
        <S.Banner>
          {banner_label_link ? (
            <S.BannerLabelLink
              to={`/${banner_label_link?.cached_url?.replace(/^\//, '')}`}
            >
              {banner_label}
            </S.BannerLabelLink>
          ) : (
            <S.BannerLabel>{banner_label}</S.BannerLabel>
          )}

          {banner_link && banner_link_label ? (
            <S.BannerLink
              to={`/${banner_link?.cached_url?.replace(/^\//, '')}`}
            >
              <S.BreadSmall>
                {banner_text} <S.BannerSpan>{banner_link_label}</S.BannerSpan>
              </S.BreadSmall>
            </S.BannerLink>
          ) : (
            <S.BreadSmall>{banner_text}</S.BreadSmall>
          )}
        </S.Banner>
      )}

      <S.Header
        {...storyblokEditable(settings)}
        $isScrolled={isScrolled}
        $position={position}
        $banner={banner && globalBannerSetting}
        ref={headerRef}
        $desktopPosition={desktopPosition}
      >
        <MainInner>
          <Flex $justify="between" $align="center" $width="100%">
            <S.Hamburger
              onClick={() => setIsOpen(true)}
              $isScrolled={isScrolled}
              $textColor={text_color}
              aria-label="Open navigation"
              aria-haspopup="true"
              aria-expanded={isOpen}
            >
              <span></span>
              <span></span>
              <span></span>
            </S.Hamburger>
            <Link to={slug ? slug : '/'}>
              <S.Logo
                src={modLogo}
                alt="Logo"
                $isScrolled={isScrolled}
                $textColor={text_color}
                width={180}
                height={36}
              />
            </Link>
          </Flex>
        </MainInner>

        {isOpen && <S.Overlay onClick={handleBackgroundClick} />}

        <S.Nav open={isOpen}>
          <AnimatePresence>
            {!openProduct && isOpen && (
              <S.NavCloseButton
                as={motion.button}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => {
                  setIsOpen(false);
                }}
                aria-label="Close navigation"
                tabIndex={isOpen ? 0 : -1}
              >
                <Icon src="Close" width={17} />
              </S.NavCloseButton>
            )}
          </AnimatePresence>

          <Flex $column>
            <S.Label>{productTitle}</S.Label>
            <S.NavList>
              {headerProducts?.map((link, i) => {
                return (
                  <li key={i}>
                    <NavLink
                      key={i}
                      blok={{
                        label: link.label,
                      }}
                      onMouseOver={() => setOpenProduct(link)}
                      active={openProduct?.label === link.label}
                      chevronRight
                      width="100%"
                      size="20px"
                      margin={link.margin_top ? '25px 0 0' : '0'}
                      tabIndex={isOpen ? 0 : -1}
                    />
                  </li>
                );
              })}
            </S.NavList>
          </Flex>

          <Flex
            $width="100%"
            $column
            $desktopDir="row"
            $desktopGap={40}
            $breakpoint="l"
            onMouseEnter={() => setOpenProduct(undefined)}
          >
            <S.SmallNavList>
              <li>
                <S.Label>{links1Title}</S.Label>
              </li>
              {links1?.map((link, i) => (
                <li key={i}>
                  <NavLink
                    orangeHover
                    key={i}
                    blok={link}
                    size="16px"
                    tabIndex={isOpen ? 0 : -1}
                  />
                </li>
              ))}
            </S.SmallNavList>

            <S.SmallNavList>
              <li>
                <S.Label>{links2Title}</S.Label>
              </li>
              {links2?.map((link, i) => (
                <li key={i}>
                  <NavLink
                    orangeHover
                    key={i}
                    blok={link}
                    size="16px"
                    tabIndex={isOpen ? 0 : -1}
                  />
                </li>
              ))}
            </S.SmallNavList>
          </Flex>
          <Flex
            $align={'center'}
            $gap={5}
            $margin={'-50px 0 0'}
            $marginDesktop={'0'}
          >
            <S.LangLink
              to="/"
              aria-label="Change language to English"
              $active={!slug}
              tabIndex={isOpen ? 0 : -1}
            >
              ENG
            </S.LangLink>
            <BreadNormal $color="lineGrey">|</BreadNormal>
            <S.LangLink
              to="/us"
              aria-label="Change language to English"
              $active={slug === '/us'}
              tabIndex={isOpen ? 0 : -1}
            >
              US
            </S.LangLink>
            <BreadNormal $color="lineGrey">|</BreadNormal>
            <S.LangLink
              to="/de"
              aria-label="Change language to Deutsch"
              $active={slug === '/de'}
              tabIndex={isOpen ? 0 : -1}
            >
              GER
            </S.LangLink>
          </Flex>
        </S.Nav>

        <S.SubNav open={openProduct !== undefined}>
          <Absolute $top={24} $left={16} $right={16}>
            <Flex $justify="between" $align="center" $width="100%">
              <S.SubNavCloseButton
                onClick={() => setOpenProduct(undefined)}
                $mobile
                aria-label="Go back"
                tabIndex={isOpen ? 0 : -1}
              >
                <Icon src="ChevronLeft" width={17} color="black" />
              </S.SubNavCloseButton>
              <S.SubNavCloseButton
                onClick={() => {
                  setOpenProduct(undefined);
                  setIsOpen(false);
                }}
                aria-label="Close navigation"
                tabIndex={isOpen ? 0 : -1}
              >
                <Icon src="Close" width={17} color="black" />
              </S.SubNavCloseButton>
            </Flex>
          </Absolute>
          {headerProducts?.map((product, i) => {
            return (
              <S.SubNavs key={i} $active={openProduct?.label === product.label}>
                <Flex
                  $column
                  as={motion.div}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  key={openProduct?.label}
                >
                  <Flex $column>
                    <S.Label>{product.label}</S.Label>
                    <S.NavList>
                      {product.links?.map((link, i) => (
                        <li key={i}>
                          <NavLink
                            orangeHover
                            key={i}
                            blok={link}
                            size="20px"
                            tabIndex={isOpen ? 0 : -1}
                          />
                        </li>
                      ))}
                    </S.NavList>

                    <Image
                      image={product.image}
                      aspectRatio="16 / 9"
                      size="740x420"
                      margin="40px 0"
                      width="100%"
                      radius={4}
                    />
                  </Flex>
                </Flex>
              </S.SubNavs>
            );
          })}
        </S.SubNav>
      </S.Header>
    </>
  );
};

export default Header;
